import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, PlayIcon } from '@heroicons/react/24/outline';

const HeroSection = () => {
  const scrollToServices = () => {
    document.getElementById('services').scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/787630/pexels-photo-787630.jpeg')`
          }}
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 bg-primary-900/60" />
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-2 h-2 bg-accent-blue rounded-full opacity-60"
        />
        <motion.div
          animate={{ y: [0, -30, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute top-1/3 right-1/4 w-3 h-3 bg-accent-gold rounded-full opacity-40"
        />
        <motion.div
          animate={{ y: [0, -25, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-cream-100 rounded-full opacity-50"
        />
      </div>

      {/* Content */}
      <div className="relative z-10 section-padding w-full">
        <div className="container-width">
          <div className="text-center">
            {/* Tagline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mb-6"
            >
              <span className="inline-block px-6 py-2 glass-card text-cream-100 text-sm font-medium tracking-wide uppercase">
                Premium Token Liquidity Advisory
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="text-hero font-serif font-bold text-cream-100 mb-8 text-shadow-lg max-w-5xl mx-auto"
            >
              Smarter Liquidity,{' '}
              <span className="text-gradient">Realized.</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-body-lg text-cream-200 mb-12 max-w-3xl mx-auto leading-relaxed"
            >
              Advising leading protocols, investors and exchanges on token strategy, 
              market structure and liquidity performance. Elite consulting expertise 
              meets cutting-edge analytics.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
            >
              <button 
                onClick={scrollToServices}
                className="btn-primary group"
              >
                Explore Our Services
                <ChevronDownIcon className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-y-1" />
              </button>
              
              <Link to="/analytics" className="btn-secondary group">
                <PlayIcon className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
                View Analytics
              </Link>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto"
            >
              <div className="glass-card p-6 text-center">
                <div className="text-heading-lg font-bold text-cream-100 mb-2">$2.5B+</div>
                <div className="text-cream-200 text-sm">Total Value Analyzed</div>
              </div>
              <div className="glass-card p-6 text-center">
                <div className="text-heading-lg font-bold text-cream-100 mb-2">150+</div>
                <div className="text-cream-200 text-sm">Protocols Advised</div>
              </div>
              <div className="glass-card p-6 text-center">
                <div className="text-heading-lg font-bold text-cream-100 mb-2">98%</div>
                <div className="text-cream-200 text-sm">Client Satisfaction</div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer"
        onClick={scrollToServices}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center text-cream-200 hover:text-cream-100 transition-colors duration-300"
        >
          <ChevronDownIcon className="h-6 w-6 mb-2" />
          <span className="text-xs font-medium tracking-widest uppercase">Scroll</span>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;